import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { type MockJobWorkOrder } from '@/mocks/billing';
import AppLayout from '@/components/feature/AppLayout';
import { jobWorkService } from '@/services/jobWorkService';
import { getData } from '@/services/FetchNodeServices';
import { useWarehouseStore } from '@/stores/warehouseStore';
import AddSubContractorModal from './components/AddSubContractorModal';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';

type JWOStatus = MockJobWorkOrder['status'];
type TabId = 'all' | 'dashboard' | 'subcontractors' | 'itc04';

const STATUS_CONFIG: Record<JWOStatus, { label: string; bg: string; text: string }> = {
  DRAFT: { label: 'Draft', bg: 'bg-slate-100 text-slate-700 border-slate-200', text: 'text-slate-700' },
  ISSUED: { label: 'Challan Issued', bg: 'bg-blue-50 text-blue-700 border-blue-200', text: 'text-blue-700' },
  IN_PROCESS: { label: 'At Sub-Contractor', bg: 'bg-amber-50 text-amber-700 border-amber-200', text: 'text-amber-700' },
  RECEIVED: { label: 'Received', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', text: 'text-emerald-700' },
  PARTIALLY_RECEIVED: { label: 'Partial Return', bg: 'bg-teal-50 text-teal-700 border-teal-200', text: 'text-teal-700' },
  CANCELLED: { label: 'Cancelled', bg: 'bg-rose-50 text-rose-700 border-rose-200', text: 'text-rose-700' },
};

function daysBetween(d1: string, d2: string): number {
  return Math.ceil((new Date(d2).getTime() - new Date(d1).getTime()) / (1000 * 60 * 60 * 24));
}

export default function JobWorkListPage() {
  const navigate = useNavigate();
  const { selectedWarehouseId } = useWarehouseStore();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [subContractorFilter, setSubContractorFilter] = useState('All');
  const [activeTab, setActiveTab] = useState<TabId>('all');
  const [jobWorkOrders, setJobWorkOrders] = useState<MockJobWorkOrder[]>([]);
  const [parties, setParties] = useState<any[]>([]);
  const [subContractorsList, setSubContractorsList] = useState<any[]>([]);
  const [isAddSubContractorOpen, setIsAddSubContractorOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);

  const today = new Date().toISOString().slice(0, 10);

  // Load live data from Backend API
  const loadJobWorkOrders = async () => {
    setLoading(true);
    try {
      const data = await jobWorkService.getAll(selectedWarehouseId ? { warehouseId: selectedWarehouseId } : undefined);
      const filteredData = selectedWarehouseId 
        ? (data || []).filter((o: any) => !o.warehouseId || o.warehouseId === selectedWarehouseId)
        : (data || []);
      setJobWorkOrders(filteredData);

      const subRes = await jobWorkService.getSubContractors({ warehouseId: selectedWarehouseId });
      if (subRes && subRes.length > 0) {
        setSubContractorsList(subRes);
      } else {
        setSubContractorsList([]);
      }

      const partyRes = await getData('api/v1/party');
      const pList = partyRes?.data || (Array.isArray(partyRes) ? partyRes : []);
      setParties(pList.filter((p: any) => p.type === 'Supplier' || p.type === 'Both' || !p.type));
    } catch (err) {
      console.error('Error fetching job work orders or parties:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobWorkOrders();
  }, [selectedWarehouseId]);

  // Summary calculations
  const summary = useMemo(() => {
    const activeStatuses: JWOStatus[] = ['ISSUED', 'IN_PROCESS'];
    const activeOrders = jobWorkOrders.filter((o) => activeStatuses.includes(o.status));
    const materialsOut = activeOrders.length;
    const totalValueOut = activeOrders.reduce((s, o) => s + o.items.reduce((si, i) => si + i.totalValue, 0), 0);
    const pendingReturn = jobWorkOrders.filter((o) => o.expectedReturnDate < today && o.status !== 'RECEIVED' && o.status !== 'CANCELLED').length;
    const receivedThisMonth = jobWorkOrders.filter((o) => {
      if (o.status !== 'RECEIVED' && o.status !== 'PARTIALLY_RECEIVED') return false;
      const rd = o.actualReturnDate || o.date;
      return rd.slice(0, 7) === today.slice(0, 7);
    }).length;
    return { materialsOut, totalValueOut, pendingReturn, receivedThisMonth };
  }, [today, jobWorkOrders]);

  const statusFilterOptions: SearchableOption[] = useMemo(() => [
    { value: 'All', label: 'All Status' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'ISSUED', label: 'Issued' },
    { value: 'IN_PROCESS', label: 'In Process' },
    { value: 'RECEIVED', label: 'Received' },
    { value: 'PARTIALLY_RECEIVED', label: 'Partially Received' },
    { value: 'CANCELLED', label: 'Cancelled' }
  ], []);

  const subContractorFilterOptions: SearchableOption[] = useMemo(() => [
    { value: 'All', label: 'All Sub-Contractors' },
    ...subContractorsList.map((sc) => ({ value: sc.id, label: sc.name }))
  ], [subContractorsList]);

  // Filtered orders
  const filteredOrders = useMemo(() => {
    let orders = [...jobWorkOrders];

    if (search) {
      const q = search.toLowerCase();
      orders = orders.filter((o) => o.jwoNumber.toLowerCase().includes(q) || (o.subContractorName || '').toLowerCase().includes(q));
    }

    if (statusFilter !== 'All') {
      orders = orders.filter((o) => o.status === statusFilter);
    }

    if (subContractorFilter !== 'All') {
      orders = orders.filter((o) => o.subContractorId === subContractorFilter);
    }

    return orders.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [search, statusFilter, subContractorFilter, jobWorkOrders]);

  // Dashboard — Materials at Sub-Contractors
  const materialsBySubContractor = useMemo(() => {
    const activeJWOs = jobWorkOrders.filter((o) => o.status === 'IN_PROCESS' || o.status === 'ISSUED');
    const grouped: Record<string, { subContractorId: string; subContractorName: string; totalItems: number; totalQty: number; totalValue: number; oldestJWO: MockJobWorkOrder | null; isOverdue: boolean }> = {};
    activeJWOs.forEach((jwo) => {
      const key = jwo.subContractorId;
      if (!grouped[key]) {
        grouped[key] = { subContractorId: key, subContractorName: jwo.subContractorName, totalItems: 0, totalQty: 0, totalValue: 0, oldestJWO: null, isOverdue: false };
      }
      const g = grouped[key];
      jwo.items.forEach((item) => {
        g.totalItems += 1;
        g.totalQty += item.sentQty;
        g.totalValue += item.totalValue;
      });
      if (!g.oldestJWO || jwo.date < g.oldestJWO.date) g.oldestJWO = jwo;
      if (jwo.expectedReturnDate < today) g.isOverdue = true;
    });
    return Object.values(grouped);
  }, [today, jobWorkOrders]);

  // Dashboard — Status Overview
  const statusCounts = useMemo(() => {
    const counts: Record<JWOStatus, number> = { DRAFT: 0, ISSUED: 0, IN_PROCESS: 0, RECEIVED: 0, PARTIALLY_RECEIVED: 0, CANCELLED: 0 };
    jobWorkOrders.forEach((o) => { if (counts[o.status] !== undefined) counts[o.status]++; });
    return counts;
  }, [jobWorkOrders]);

  // Dashboard — Overdue JWOs
  const overdueJWOs = useMemo(() => {
    return jobWorkOrders.filter((o) => (o.status === 'IN_PROCESS' || o.status === 'ISSUED') && o.expectedReturnDate < today);
  }, [today, jobWorkOrders]);

  // Dashboard — Recent Activity
  const recentActivity = useMemo(() => {
    const events: { text: string; date: string }[] = [];
    jobWorkOrders.forEach((jwo) => {
      if (jwo.status === 'RECEIVED' || jwo.status === 'PARTIALLY_RECEIVED') {
        const totalReceived = jwo.items.reduce((s, i) => s + i.receivedQty, 0);
        events.push({ text: `${jwo.jwoNumber} — ${totalReceived} ${jwo.items[0]?.unit || 'items'} received from ${jwo.subContractorName}`, date: jwo.actualReturnDate || jwo.date });
      }
      if (jwo.challanNumber && jwo.status === 'ISSUED') {
        events.push({ text: `${jwo.jwoNumber} — Challan issued to ${jwo.subContractorName}`, date: jwo.date });
      }
    });
    return events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 5);
  }, [jobWorkOrders]);

  // Sub-Contractors — Dynamic Rating & Stats derived from real orders and master parties
  const subContractorStats = useMemo(() => {
    const map: Record<string, { id: string; name: string; gstin: string; phone: string; city: string; address: string; activeCount: number; totalSent: number; totalReceived: number; totalRejected: number; totalValueSent: number }> = {};
    
    // First pre-populate map with active dedicated sub-contractors for selected warehouse (no supplier fallback)
    const masterList = subContractorsList;
    masterList.forEach((p) => {
      map[p.id] = {
        id: p.id,
        name: p.name || 'Sub-contractor',
        gstin: p.gstin || 'N/A',
        phone: p.phone || '',
        city: p.city || '',
        address: p.address || '',
        activeCount: 0,
        totalSent: 0,
        totalReceived: 0,
        totalRejected: 0,
        totalValueSent: 0,
      };
    });

    jobWorkOrders.forEach((o) => {
      const id = o.subContractorId || 'unknown';
      if (!map[id]) {
        map[id] = {
          id,
          name: o.subContractorName || 'Sub-contractor',
          gstin: o.subContractorGSTIN || 'N/A',
          phone: '',
          city: '',
          address: '',
          activeCount: 0,
          totalSent: 0,
          totalReceived: 0,
          totalRejected: 0,
          totalValueSent: 0,
        };
      }
      const sc = map[id];
      if (o.status === 'IN_PROCESS' || o.status === 'ISSUED') sc.activeCount += 1;
      (o.items || []).forEach((i) => {
        sc.totalSent += i.sentQty || 0;
        sc.totalReceived += i.receivedQty || 0;
        sc.totalRejected += i.rejectedQty || 0;
        sc.totalValueSent += i.totalValue || 0;
      });
    });

    return Object.values(map).map((sc) => {
      const rating = sc.totalSent > 0 ? ((sc.totalReceived - sc.totalRejected) / sc.totalSent) * 100 : 100;
      let ratingLabel = 'Excellent';
      let ratingStars = '⭐⭐⭐';
      let ratingColor = 'text-emerald-600';
      if (sc.totalSent > 0) {
        if (rating < 95 && rating >= 90) { ratingLabel = 'Good'; ratingStars = '⭐⭐'; ratingColor = 'text-teal-600'; }
        else if (rating < 90) { ratingLabel = 'Needs Improvement'; ratingStars = '⭐'; ratingColor = 'text-amber-600'; }
      }
      return { ...sc, rating, ratingLabel, ratingStars, ratingColor };
    });
  }, [jobWorkOrders, parties, subContractorsList]);

  // Handle issue challan via API
  const handleIssueChallan = async (jwo: MockJobWorkOrder) => {
    try {
      const res = await jobWorkService.issueChallan(jwo.id);
      if (res && res.success) {
        await loadJobWorkOrders();
      } else {
        const year = new Date().getFullYear();
        const seq = String(jobWorkOrders.filter((o) => o.challanNumber).length + 1).padStart(3, '0');
        jwo.challanNumber = `DC-JW-${year}-${seq}`;
        jwo.status = 'ISSUED';
        setJobWorkOrders([...jobWorkOrders]);
      }
    } catch (e) {
      console.error('handleIssueChallan error:', e);
    }
  };

  // Handle cancel via API
  const handleCancel = async (jwo: MockJobWorkOrder) => {
    try {
      const res = await jobWorkService.cancel(jwo.id);
      if (res && res.success) {
        await loadJobWorkOrders();
      } else {
        jwo.status = 'CANCELLED';
        setJobWorkOrders([...jobWorkOrders]);
      }
    } catch (e) {
      console.error('handleCancel error:', e);
    }
  };

  const handleRecordReceipt = (jwo: MockJobWorkOrder) => {
    navigate(`/manufacturing/jobwork/${jwo.id}?action=receipt`);
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-[#f8fafc] py-6 px-4 md:px-6 w-full">
        <div className="w-full">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[#1e293b]">Job Work / Subcontracting</h1>
              <p className="text-sm text-[#64748b] mt-0.5">Track materials sent out for processing and challans</p>
            </div>
            
            {/* Top Right Action Button */}
            <div className="flex items-center gap-2.5 flex-wrap sm:ml-auto">
              <Link
                to="/manufacturing/jobwork/new"
                className="px-4 py-2 rounded-lg bg-[#2563eb] hover:bg-blue-700 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5 shadow-sm"
              >
                <i className="ri-add-line text-base" /> New Job Work Order
              </Link>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3.5">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-amber-100 text-amber-600 shrink-0">
                  <i className="ri-truck-line text-xl" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700">{summary.materialsOut}</p>
                  <p className="text-xs font-semibold text-amber-600 mt-0.5">Materials Out</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3.5">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-indigo-100 text-indigo-600 shrink-0">
                  <i className="ri-money-rupee-circle-line text-xl" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-indigo-700">₹{summary.totalValueOut.toLocaleString('en-IN')}</p>
                  <p className="text-xs font-semibold text-indigo-600 mt-0.5">Total Value Out</p>
                </div>
              </div>
            </div>

            <div className={`bg-white border rounded-xl p-5 shadow-sm ${summary.pendingReturn > 0 ? 'border-rose-300 bg-rose-50/30' : 'border-slate-200'}`}>
              <div className="flex items-center gap-3.5">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-rose-100 text-rose-600 shrink-0">
                  <i className="ri-alarm-warning-line text-xl" />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${summary.pendingReturn > 0 ? 'text-rose-700' : 'text-slate-800'}`}>{summary.pendingReturn}</p>
                  <p className="text-xs font-semibold text-rose-600 mt-0.5">{summary.pendingReturn > 0 ? 'Pending Overdue' : 'Pending Return'}</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3.5">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 shrink-0">
                  <i className="ri-checkbox-circle-line text-xl" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-700">{summary.receivedThisMonth}</p>
                  <p className="text-xs font-semibold text-emerald-600 mt-0.5">Received This Month</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Bar */}
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-1.5 mb-6 w-fit shadow-sm">
            {([
              { id: 'all' as TabId, label: 'All JWOs', icon: 'ri-file-list-3-line' },
              { id: 'dashboard' as TabId, label: 'Dashboard', icon: 'ri-dashboard-line' },
              { id: 'subcontractors' as TabId, label: 'Sub-Contractors', icon: 'ri-team-line' },
              { id: 'itc04' as TabId, label: 'ITC-04', icon: 'ri-file-chart-line' },
            ]).map((tab) => (
              <button
                key={tab.id}
                onClick={() => tab.id === 'itc04' ? navigate('/manufacturing/jobwork/itc04') : setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap transition-colors ${activeTab === tab.id && tab.id !== 'itc04' ? 'bg-[#2563eb] text-white shadow-xs' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
              >
                <i className={`${tab.icon} text-sm`} /> {tab.label}
              </button>
            ))}
          </div>

          {/* ── ALL JWOs TAB ── */}
          {activeTab === 'all' && (
            <>
              {/* Filter Bar */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 shadow-sm">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="relative">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
                        <i className="ri-search-line text-sm" />
                      </div>
                      <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search JWO number or sub-contractor..."
                        className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5 flex-wrap">
                    <div className="w-44">
                      <SearchableSelect
                        value={statusFilter}
                        onChange={(val) => setStatusFilter(val)}
                        options={statusFilterOptions}
                        placeholder="All Status"
                      />
                    </div>

                    <div className="w-52">
                      <SearchableSelect
                        value={subContractorFilter}
                        onChange={(val) => setSubContractorFilter(val)}
                        options={subContractorFilterOptions}
                        placeholder="All Sub-Contractors"
                        searchPlaceholder="Search sub-contractor..."
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50/70 border-b border-slate-200">
                        <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">JWO Number</th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Sub-Contractor</th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Job Description</th>
                        <th className="text-center px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Items</th>
                        <th className="text-right px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Value Out</th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Sent Date</th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Expected Return</th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Status</th>
                        <th className="text-center px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-10 text-center text-sm text-slate-400">
                            <div className="flex items-center justify-center gap-2">
                              <i className="ri-loader-4-line animate-spin text-xl text-blue-600" />
                              <span>Loading job work orders...</span>
                            </div>
                          </td>
                        </tr>
                      ) : filteredOrders.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-10 text-center text-sm text-slate-400">
                            <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-slate-50 border border-slate-200 mb-3">
                              <i className="ri-inbox-line text-xl text-slate-300" />
                            </div>
                            No job work orders found matching your filters
                          </td>
                        </tr>
                      ) : (
                        filteredOrders.map((jwo) => {
                          const statusCfg = STATUS_CONFIG[jwo.status] || { label: jwo.status, bg: 'bg-slate-100 text-slate-700 border-slate-200' };
                          const isOverdue = (jwo.status === 'IN_PROCESS' || jwo.status === 'ISSUED') && jwo.expectedReturnDate < today;
                          const totalValue = jwo.items.reduce((s, i) => s + i.totalValue, 0);
                          const totalItems = jwo.items.length;
                          const overdueDays = isOverdue ? daysBetween(jwo.expectedReturnDate, today) : 0;

                          return (
                            <tr key={jwo.id} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors">
                              <td className="px-4 py-3 whitespace-nowrap">
                                <Link to={`/manufacturing/jobwork/${jwo.id}`} className="text-sm font-bold text-[#2563eb] hover:underline">
                                  {jwo.jwoNumber}
                                </Link>
                                {(jwo as any).autoCreated && (
                                  <p className="text-[10px] text-indigo-600 font-semibold mt-0.5" title="Raised from a job work stage of the routing">
                                    <i className="ri-route-line mr-0.5" />
                                    {(jwo as any).productionOrderNumber || 'Production order'} · stage {(jwo as any).stageNumber}
                                  </p>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <p className="font-bold text-[#1e293b]">{jwo.subContractorName}</p>
                                <p className="text-[11px] text-slate-400 font-medium">{jwo.subContractorGSTIN}</p>
                              </td>
                              <td className="px-4 py-3 max-w-[200px]">
                                <p className="text-slate-700 truncate font-medium" title={jwo.jobDescription}>{jwo.jobDescription}</p>
                              </td>
                              <td className="px-4 py-3 text-center whitespace-nowrap">
                                <span className="font-bold text-[#1e293b]">{totalItems}</span>
                                <span className="text-slate-400 text-xs ml-1 font-medium">items</span>
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-[#1e293b] whitespace-nowrap">₹{totalValue.toLocaleString('en-IN')}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-xs font-medium text-slate-600">
                                {new Date(jwo.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`text-xs font-semibold ${isOverdue ? 'text-rose-600 font-bold' : 'text-slate-600'}`}>
                                  {new Date(jwo.expectedReturnDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </span>
                                {isOverdue && (
                                  <span className="block text-[10px] text-rose-500 font-bold">{overdueDays} days overdue</span>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-semibold border ${statusCfg.bg}`}>
                                  {statusCfg.label}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <div className="flex items-center justify-center gap-1.5">
                                  <Link
                                    to={`/manufacturing/jobwork/${jwo.id}`}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 cursor-pointer transition-colors"
                                    title="View"
                                  >
                                    <i className="ri-eye-line text-sm" />
                                  </Link>
                                  {jwo.status === 'DRAFT' && (
                                    <>
                                      <button
                                        onClick={() => navigate('/sales/challans', { state: { fromJWO: jwo } })}
                                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 cursor-pointer transition-colors"
                                        title="Issue Challan"
                                      >
                                        <i className="ri-file-list-3-line text-sm" />
                                      </button>
                                      <button
                                        onClick={() => handleCancel(jwo)}
                                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 cursor-pointer transition-colors"
                                        title="Cancel"
                                      >
                                        <i className="ri-close-circle-line text-sm" />
                                      </button>
                                    </>
                                  )}
                                  {(jwo.status === 'IN_PROCESS' || jwo.status === 'PARTIALLY_RECEIVED') && (
                                    <button
                                      onClick={() => handleRecordReceipt(jwo)}
                                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 cursor-pointer transition-colors"
                                      title="Record Receipt"
                                    >
                                      <i className="ri-inbox-archive-line text-sm" />
                                    </button>
                                  )}
                                  {jwo.challanNumber && (
                                    <button
                                      onClick={() => window.print()}
                                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 cursor-pointer transition-colors"
                                      title="Print Challan"
                                    >
                                      <i className="ri-printer-line text-sm" />
                                    </button>
                                  )}
                                  {isOverdue && jwo.status === 'IN_PROCESS' && (
                                    <button
                                      onClick={() => {
                                        const ToastContext = (window as any).__toast;
                                        if (ToastContext?.success) ToastContext.success(`Reminder sent to ${jwo.subContractorName}`);
                                      }}
                                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 cursor-pointer transition-colors"
                                      title="Send Reminder"
                                    >
                                      <i className="ri-notification-3-line text-sm" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* ── DASHBOARD TAB ── */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              {/* Section 1 — Materials at Sub-Contractors */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="px-5 py-3.5 border-b border-slate-200 bg-slate-50/70">
                  <h2 className="text-sm font-bold text-[#1e293b]">Materials at Sub-Contractors</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50/70 border-b border-slate-200">
                        <th className="text-left px-4 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Sub-Contractor</th>
                        <th className="text-center px-4 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Items</th>
                        <th className="text-right px-4 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Total Qty</th>
                        <th className="text-right px-4 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Value</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Oldest JWO</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {materialsBySubContractor.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-slate-400 font-medium text-xs">No materials currently at sub-contractors</td>
                        </tr>
                      ) : (
                        materialsBySubContractor.map((g) => (
                          <tr key={g.subContractorId} className={`hover:bg-slate-50/60 transition-colors ${g.isOverdue ? 'bg-rose-50/30' : ''}`}>
                            <td className="px-4 py-3 font-bold text-[#1e293b]">{g.subContractorName}</td>
                            <td className="px-4 py-3 text-center font-bold text-slate-700">{g.totalItems}</td>
                            <td className="px-4 py-3 text-right text-slate-600 font-medium">{g.totalQty.toLocaleString('en-IN')}</td>
                            <td className="px-4 py-3 text-right font-bold text-[#1e293b]">₹{g.totalValue.toLocaleString('en-IN')}</td>
                            <td className="px-4 py-3">
                              {g.oldestJWO && (
                                <Link to={`/manufacturing/jobwork/${g.oldestJWO.id}`} className="text-[#2563eb] hover:underline font-bold text-xs whitespace-nowrap">
                                  {g.oldestJWO.jwoNumber}
                                </Link>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {g.isOverdue ? (
                                <span className="inline-flex items-center gap-1 text-[11px] px-2.5 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200 font-bold">
                                  <i className="ri-timer-line" /> Overdue
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[11px] px-2.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold">
                                  On Track
                                </span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Section 2 — Status Overview */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <h2 className="text-sm font-bold text-[#1e293b] mb-4">Status Overview</h2>
                <div className="space-y-3">
                  {([
                    { key: 'DRAFT', label: 'Draft', count: statusCounts.DRAFT, barColor: 'bg-slate-300' },
                    { key: 'ISSUED', label: 'Issued', count: statusCounts.ISSUED, barColor: 'bg-blue-500' },
                    { key: 'IN_PROCESS', label: 'In Process', count: statusCounts.IN_PROCESS, barColor: 'bg-amber-500' },
                    { key: 'RECEIVED', label: 'Received', count: statusCounts.RECEIVED, barColor: 'bg-emerald-500' },
                  ] as const).map((s) => (
                    <div key={s.key} className="flex items-center gap-3">
                      <span className="w-24 text-xs font-bold text-slate-600 shrink-0">{s.label}</span>
                      <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${s.barColor} transition-all flex items-center justify-end px-2`}
                          style={{ width: `${jobWorkOrders.length > 0 ? Math.max((s.count / jobWorkOrders.length) * 100, 5) : 0}%` }}
                        >
                          <span className="text-[10px] font-bold text-white">{s.count}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-3 mt-5 pt-4 border-t border-slate-100">
                  <div className="text-center">
                    <p className="text-xl font-bold text-teal-600">{statusCounts.PARTIALLY_RECEIVED}</p>
                    <p className="text-xs text-slate-500 font-medium">Partially Received</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-rose-600">{statusCounts.CANCELLED}</p>
                    <p className="text-xs text-slate-500 font-medium">Cancelled</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-[#1e293b]">{jobWorkOrders.length}</p>
                    <p className="text-xs text-slate-500 font-medium">Total JWOs</p>
                  </div>
                </div>
              </div>

              {/* Section 3 — Overdue JWOs */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="px-5 py-3.5 border-b border-slate-200 bg-slate-50/70">
                  <h2 className="text-sm font-bold text-[#1e293b] flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-rose-500 inline-block" />
                    Overdue Job Work Orders
                    <span className="text-xs font-semibold text-rose-600">({overdueJWOs.length})</span>
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50/70 border-b border-slate-200">
                        <th className="text-left px-4 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">JWO No</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Sub-Contractor</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Item</th>
                        <th className="text-right px-4 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Days Overdue</th>
                        <th className="text-center px-4 py-2.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {overdueJWOs.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-8 text-center text-emerald-700 text-xs font-semibold">
                            <i className="ri-check-line mr-1 text-sm" /> No overdue JWOs — all processing on track
                          </td>
                        </tr>
                      ) : (
                        overdueJWOs.map((jwo) => {
                          const overdueDays = daysBetween(jwo.expectedReturnDate, today);
                          return (
                            <tr key={jwo.id} className="hover:bg-rose-50/40 bg-rose-50/20">
                              <td className="px-4 py-3">
                                <Link to={`/manufacturing/jobwork/${jwo.id}`} className="text-[#2563eb] font-bold hover:underline whitespace-nowrap">{jwo.jwoNumber}</Link>
                              </td>
                              <td className="px-4 py-3 text-slate-800 font-semibold">{jwo.subContractorName}</td>
                              <td className="px-4 py-3 text-slate-600 font-medium">{jwo.items[0]?.itemName || '—'}</td>
                              <td className="px-4 py-3 text-right font-bold text-rose-600">{overdueDays} days</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => {
                                      const ToastContext = (window as any).__toast;
                                      if (ToastContext?.success) ToastContext.success(`Reminder sent to ${jwo.subContractorName}`);
                                    }}
                                    className="px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 cursor-pointer whitespace-nowrap transition-colors"
                                  >
                                    <i className="ri-notification-3-line mr-1" /> Send Reminder
                                  </button>
                                  <button
                                    onClick={() => handleRecordReceipt(jwo)}
                                    className="px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 cursor-pointer whitespace-nowrap transition-colors"
                                  >
                                    <i className="ri-inbox-archive-line mr-1" /> Record Receipt
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Section 4 — Recent Activity */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <h2 className="text-sm font-bold text-[#1e293b] mb-3">Recent Activity</h2>
                <div className="space-y-0">
                  {recentActivity.map((ev, idx) => (
                    <div key={idx} className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0">
                      <div className="w-2 h-2 rounded-full bg-[#2563eb] shrink-0" />
                      <span className="text-sm text-slate-700 font-medium flex-1">{ev.text}</span>
                      <span className="text-xs text-slate-400 font-medium whitespace-nowrap">
                        {new Date(ev.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                  ))}
                  {recentActivity.length === 0 && (
                    <p className="text-xs text-slate-400 py-4 text-center font-medium">No recent activity</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── SUB-CONTRACTORS TAB ── */}
          {activeTab === 'subcontractors' && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <div className="p-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 bg-slate-50/70">
                <div>
                  <h3 className="text-sm font-bold text-[#1e293b]">Sub-Contractors Master</h3>
                  <p className="text-xs text-slate-500 font-medium">Manage registered sub-contractors and monitor active job work orders</p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAddSubContractorOpen(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-[#2563eb] hover:bg-blue-700 text-white rounded-lg cursor-pointer shadow-sm transition-colors"
                >
                  <i className="ri-user-add-line text-sm" /> Add Sub-Contractor
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50/70 border-b border-slate-200">
                      <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">Name</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">GSTIN</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">Phone</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">City / Location</th>
                      <th className="text-center px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">Active JWOs</th>
                      <th className="text-right px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">Total Value Sent</th>
                      <th className="text-center px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">Rating</th>
                      <th className="text-center px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {subContractorStats.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-slate-400 font-medium text-xs">
                          No sub-contractors found for this warehouse. Click <strong className="text-[#2563eb]">Add Sub-Contractor</strong> to add one.
                        </td>
                      </tr>
                    ) : (
                      subContractorStats.map((sc) => (
                        <tr key={sc.id} className="hover:bg-slate-50/60 transition-colors">
                          <td className="px-4 py-3 font-bold text-[#1e293b]">{sc.name}</td>
                          <td className="px-4 py-3 font-mono text-xs text-slate-500 font-medium">{sc.gstin}</td>
                          <td className="px-4 py-3 text-slate-700 text-xs font-medium">{sc.phone || 'N/A'}</td>
                          <td className="px-4 py-3 text-slate-700 text-xs font-medium">{sc.city || sc.address || 'N/A'}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`font-bold ${sc.activeCount > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{sc.activeCount}</span>
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-[#1e293b]">₹{sc.totalValueSent.toLocaleString('en-IN')}</td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex flex-col items-center gap-0.5">
                              <span className="text-xs">{sc.ratingStars}</span>
                              <span className={`text-[11px] font-bold ${sc.ratingColor}`}>{sc.ratingLabel}</span>
                              <span className="text-[10px] text-slate-400 font-medium">{sc.rating.toFixed(1)}%</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => { setSubContractorFilter(sc.id); setActiveTab('all'); }}
                                className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 cursor-pointer transition-colors"
                                title="View JWOs"
                              >
                                <i className="ri-eye-line text-sm" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      <AddSubContractorModal
        isOpen={isAddSubContractorOpen}
        onClose={() => setIsAddSubContractorOpen(false)}
        onSuccess={(newSub) => {
          setSubContractorsList((prev) => [newSub, ...prev]);
        }}
      />
    </AppLayout>
  );
}